import { useCallback } from 'react';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService } from 'ui-components/services/notification-service';

import { generateHarmonizationCode } from '../harmonization-api';

/**
 * @typedef {object} ProductInput
 * @property {number} productId
 * @property {string} productName
 * @property {string} productDescription
 * @property {string} customsDescription
 */

/**
 * Hook for managing bulk harmonization via multiple parallel API calls
 * TODO: This was a stop gap for pushing out a POC feature quickly and should be revisited for performance and scalability
 * i.e. create a proper bulk workflow instead of firing many requests in parallel from the client
 * @returns {{
 *   results: Array<import('./harmonization-api').BulkResultItem>,
 *   isLoading: boolean,
 *   generateForProducts: (products: Array<ProductInput>, workflowGuid: string) => Promise<void>,
 * }}
 */
export const useBulkHarmonization = () => {
  const [results, setResults] = useAsyncSafeState([]);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);

  const generateForProducts = useCallback(
    async (products, workflowGuid) => {
      setIsLoading(true);
      setResults([]);

      try {
        const promises = products.map((product) =>
          generateHarmonizationCode(product.productId, product.productDescription, product.customsDescription, workflowGuid),
        );

        const settledResults = await Promise.allSettled(promises);

        const transformedResults = settledResults.map((result, index) => {
          const product = products[index];

          // TODO: reconsider how we build this object and pass it around less
          if (result.status === 'fulfilled') {
            return {
              productId: result.value.productId,
              productName: product.name,
              suggestions: result.value.suggestions ?? [],
              hadDescription: Boolean(product.productDescription || product.customsDescription),
            };
          }

          const errorMessage = result.reason?.message ?? 'Unknown error';
          return {
            productId: product.productId,
            productName: product.name,
            suggestions: [],
            error: errorMessage,
            hadDescription: Boolean(product.productDescription || product.customsDescription),
          };
        });

        setResults(transformedResults);
      } catch {
        NotificationService.showGenericFailureNotification();
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, setResults],
  );

  return {
    results,
    isLoading,
    generateForProducts,
  };
};
